import { useEffect, useState } from 'react'
import { api, type OperationsCapabilities } from '../api'
import { systemSections, type SystemSection } from '../pages/Operations'
import { SideNav } from './SideNav'

/**
 * Settings, listed down the left with the screen chosen beside it.
 *
 * Settings is a set of places an administrator moves between, and a dropdown that
 * closes on every choice made them find the menu again for each one. System's own
 * parts are listed here too, in place of a row of tabs across its page: they are
 * settings like the rest, and two levels of navigation in two directions made the
 * reader work out which one the page was obeying.
 *
 * Drawn as the Help index is drawn: sections ruled off from each other under their
 * own headings, the entries of each hanging from a guide line, and the one showing
 * marked in teal. Every entry sits under a heading, so no section runs into the next.
 * Labels only, with each screen's line of detail on hover.
 */

export interface SettingsEntry {
  id: string
  label: string
  blurb: string
}

export interface SettingsGroup {
  title: string
  /** The section's line of detail, on hover. */
  hint?: string
  entries: SettingsEntry[]
}

export function SettingsNav({
  groups,
  system,
  current,
  section,
  onChoose,
  onSystem,
}: {
  groups: SettingsGroup[]
  /** The System screen, where this person may open it; its parts are listed as a section. */
  system?: SettingsEntry
  /** The screen showing. */
  current: string
  /** The part of System showing, when System is. */
  section: SystemSection
  onChoose: (id: string) => void
  onSystem: (section: SystemSection) => void
}) {
  // Which of System's parts this appliance offers: two exist only on a demonstration one.
  const [can, setCan] = useState<OperationsCapabilities | null>(null)
  useEffect(() => {
    if (!system) return
    let live = true
    api
      .operationsCapabilities()
      .then((found) => live && setCan(found))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [system?.id])

  const sections = [
    ...groups.map((group) => ({
      title: group.title,
      hint: group.hint,
      entries: group.entries.map((entry) => ({
        key: entry.id,
        label: entry.label,
        blurb: entry.blurb,
        showing: entry.id === current,
        select: () => onChoose(entry.id),
      })),
    })),
    ...(system
      ? [
          {
            title: system.label,
            hint: system.blurb,
            entries: systemSections(can).map(([id, label]) => ({
              key: id,
              label,
              blurb: '',
              showing: current === system.id && section === id,
              select: () => onSystem(id),
            })),
          },
        ]
      : []),
  ]

  return <SideNav label="Settings" sections={sections} />
}
